import React, { useState } from 'react';
import { z } from 'zod';
import { useForm, Form, FormItem, FormLabel, FormControl, FormMessage } from './Form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import { Input } from './Input';
import { useResetUserPassword } from '../helpers/useAdminApi';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { AdminUserView } from '../endpoints/admin/users_GET.schema';
import styles from './AdminResetPasswordDialog.module.css';

const formSchema = z.object({
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
});

interface AdminResetPasswordDialogProps {
  user: AdminUserView | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AdminResetPasswordDialog = ({
  user,
  open,
  onOpenChange,
}: AdminResetPasswordDialogProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const { mutateAsync: resetPassword, isPending } = useResetUserPassword();

  const form = useForm({
    defaultValues: {
      newPassword: '',
    },
    schema: formSchema,
  });

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!user) return;

    try {
      await resetPassword({
        userId: user.id,
        newPassword: values.newPassword,
      });
      toast.success(`Password reset for ${user.displayName}`);
      onOpenChange(false);
      form.setValues({ newPassword: '' });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to reset password');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={styles.dialogContent}>
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            Enter a new password for <strong>{user?.displayName}</strong> ({user?.email}).
            <br />
            This will immediately invalidate their current password.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className={styles.form}>
            <FormItem name="newPassword">
              <FormLabel>New Password</FormLabel>
              <div className={styles.passwordInputWrapper}>
                <FormControl>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimum 8 characters"
                    value={form.values.newPassword}
                    onChange={(e) =>
                      form.setValues((prev) => ({ ...prev, newPassword: e.target.value }))
                    }
                    disabled={isPending}
                  />
                </FormControl>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className={styles.eyeButton}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
              <FormMessage />
            </FormItem>

            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="animate-spin" size={16} />}
                Reset Password
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
